import { useEffect, useRef, useState } from 'react';
import { consolidationService } from './service';
import { duplicatedModels, getRowError } from './utils';
import type { Progress, ResultRow, Submission } from './types';

export function useConsolidation() {
  const [phase, setPhase] = useState<'form' | 'preview' | 'review' | 'execute' | 'complete'>('form');
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [rows, setRows] = useState<ResultRow[]>([]);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stopRequested, setStopRequested] = useState(false);
  const running = useRef(false);
  const stop = useRef(false);
  const mounted = useRef(true);
  const busy = phase === 'preview' || phase === 'execute';

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; stop.current = true; };
  }, []);
  useEffect(() => {
    if (!busy) return;
    const preventClose = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', preventClose);
    return () => window.removeEventListener('beforeunload', preventClose);
  }, [busy]);

  const patchRow = (index: number, patch: Partial<ResultRow>) => {
    if (mounted.current) setRows(current => current.map(row => row.index === index ? { ...row, ...patch } : row));
  };

  const preview = async (next: Submission) => {
    if (running.current) return;
    running.current = true;
    stop.current = false;
    setStopRequested(false);
    setError(null);
    setSubmission(next);
    setRows(next.pairs.map((pair, index) => ({ ...pair, index, status: 'pending', selected: false,
      candidates: { base: [], toIntegrate: [] } })));
    setProgress({ phase: 'preview', total: next.pairs.length, completed: 0, stopped: false });
    setPhase('preview');
    try {
      for (const [index, pair] of next.pairs.entries()) {
        if (stop.current || !mounted.current) break;
        patchRow(index, { status: 'previewing' });
        setProgress(current => current && { ...current, currentSku: pair.baseSku });
        try {
          const result = await consolidationService.preview(next.configuration, pair);
          patchRow(index, { ...result, index, selected: result.status === 'ready' });
        } catch (cause) { patchRow(index, { status: 'error', error: getRowError(cause) }); }
        if (mounted.current) setProgress(current => current && { ...current, completed: current.completed + 1 });
      }
    } finally {
      running.current = false;
      if (mounted.current) {
        setPhase('review');
        setProgress(current => current && { ...current, currentSku: undefined, stopped: stop.current });
      }
    }
  };

  const execute = async () => {
    if (running.current || !submission) return;
    const chosen = rows.filter(row => row.status === 'ready' && row.selected && row.selection);
    if (!chosen.length) return;
    if (duplicatedModels(chosen).length) {
      setError('Un modelo participa en varias filas seleccionadas. Selecciona una sola de esas filas para evitar consolidaciones encadenadas.');
      return;
    }
    running.current = true;
    stop.current = false;
    setStopRequested(false);
    setError(null);
    setPhase('execute');
    setProgress({ phase: 'execute', total: chosen.length, completed: 0, stopped: false });
    try {
      for (const row of chosen) {
        if (stop.current || !mounted.current) break;
        patchRow(row.index, { status: 'executing' });
        setProgress(current => current && { ...current, currentSku: row.baseSku });
        try {
          const result = await consolidationService.execute(submission.configuration, row.selection!);
          patchRow(row.index, { status: result.status, error: result.error, integration: result.integration, selected: false });
        } catch (cause) {
          patchRow(row.index, { status: 'error', error: getRowError(cause, true), selected: false });
        }
        if (mounted.current) setProgress(current => current && { ...current, completed: current.completed + 1 });
      }
    } finally {
      running.current = false;
      if (mounted.current) {
        setPhase(stop.current ? 'review' : 'complete');
        setProgress(current => current && { ...current, currentSku: undefined, stopped: stop.current });
      }
    }
  };

  return { phase, submission, rows, progress, error, busy, stopRequested, preview, execute, patchRow,
    requestStop: () => { stop.current = true; setStopRequested(true); },
    newRun: () => { if (!running.current) { setRows([]); setSubmission(null); setProgress(null); setError(null); setPhase('form'); } },
  };
}
